/**
 * The API's injected page theme declares `--custom-primary-button-*` only when the account
 * customized its button colors; a header customization on its own doesn't imply them. Parsed rather
 * than string-matched so the lookup stays scoped to the theme block: user-authored header content
 * naming the var can't false-positive, and it doesn't depend on how the backend template happens to
 * order or quote the `<style>` tag's attributes.
 */
export const htmlHasCustomButtonColors = (html?: string) => {
	if (!html) {
		return false;
	}
	const parsed = new DOMParser().parseFromString(html, 'text/html');
	return (parsed.getElementById('customPageTheme')?.textContent ?? '').includes('--custom-primary-button-');
};

/**
 * Builds a ref-counted applier for a set of CSS custom properties on `<body>`. Ref-counted so that
 * overlapping consumers (an animated route swap mounting the next page before the previous one
 * unmounts, say) can't strip vars a still-mounted consumer depends on.
 */
export const createBodyVarApplier = (vars: Record<string, string>) => {
	let activeConsumers = 0;

	return () => {
		activeConsumers += 1;
		Object.entries(vars).forEach(([name, value]) => {
			document.body.style.setProperty(name, value);
		});

		return () => {
			activeConsumers -= 1;
			if (activeConsumers > 0) {
				return;
			}
			Object.keys(vars).forEach(name => {
				document.body.style.removeProperty(name);
			});
		};
	};
};
